import { useContext } from 'react'
import { Link, NavLink, Route, Routes } from 'react-router-dom'
import TopBar from '../Sidebar/TopBar'
import { MenuItem, NAV_ROW_GRID_CLASS, LoggedInAs } from '../Sidebar/Sidebar'
import routes from '../Router/routes'
import { userContext } from '../../context/UserContext'
import { FOCUS_VISIBLE_RING_OFFSET } from '../../lib/a11yFocus'
import SelectOrganisation from '../../pages/SelectOrganisation'
import CompanySupport from '../../pages/CompanySupport'
import UniversalPageLayout from '../BaseComponents/UniversalPageLayout/UniversalPageLayout'
import UserSettingsSection from '../UserSettingsSection/UserSettingsSection'
import ConnectOrganisationSetting from '../ConnectOrganisationSetting/ConnectOrganisationSetting'
import AccordionSection from '../BaseComponents/AccordionSection'
import { SettingsGroup } from '../SettingsLayout/SettingsLayout'
import useUserData from '../../hooks/useUserData'
import Notifications from '../Notifications'
import SmallScreenNotice from '../SmallScreenNotice/SmallScreenNotice'

function PickerSettings() {
	const userData = useUserData()

	return (
		<UniversalPageLayout documentTitle={routes.companySettings.name} heading={{ title: 'Account Settings' }}>
			<div className='text-white'>
				<UserSettingsSection />
				{userData.canRequestUpgrade && (
					<AccordionSection id='settings-organisation' title='Organisation' defaultOpen>
						<SettingsGroup>
							<ConnectOrganisationSetting />
						</SettingsGroup>
					</AccordionSection>
				)}
			</div>
		</UniversalPageLayout>
	)
}

// B-7: the dashboard shown to a user with 2+ memberships before they choose an organisation - only
// Dashboard plus the settings/help/logout items, since every other page needs an active organisation.
function OrganisationPickerLayout() {
	const { userData } = useContext(userContext)

	if (!userData) {
		return null
	}

	return (
		<div className='flex flex-col h-dvh' style={{ background: '#374654' }}>
			<TopBar />
			<div className='flex content-stretch grow overflow-hidden'>
				<div data-testid='sidebar' className='w-1/6 md:w-1/5 p-sm bg-charcoal-700 flex flex-col shrink-0'>
					<h1 className='text-charcoal-050 mt-1 ml-4 mb-2'>
						<LoggedInAs fullName={userData.fullName} canRequestUpgrade={userData.canRequestUpgrade} />
					</h1>
					<nav className='p-md h-full flex flex-col justify-between' aria-label='Sidebar'>
						<div>
							<NavLink
								to='/'
								end
								className={({ isActive }) =>
									`${
										isActive ? 'text-charcoal-900 bg-algae-200' : 'text-charcoal-050 hover:text-white'
									} ${NAV_ROW_GRID_CLASS} p-md text-sm rounded mb-md ${FOCUS_VISIBLE_RING_OFFSET}`
								}
							>
								{({ isActive }) => (
									<>
										<span
											className={`text-center fa-solid fa-house ${isActive ? 'text-charcoal-500' : 'text-algae-200'}`}
										></span>
										<span>Dashboard</span>
									</>
								)}
							</NavLink>
						</div>
						<div>
							<MenuItem
								id='companySupport'
								name={routes.companySupport.name}
								menuName={routes.companySupport.menuName}
								path={routes.companySupport.path}
								icon={routes.companySupport.icon}
								childPages={[]}
							/>
							<MenuItem
								id='companySettings'
								name={routes.companySettings.name}
								menuName={routes.companySettings.menuName}
								path={routes.companySettings.path}
								icon={routes.companySettings.icon}
								childPages={[]}
							/>
							<Link
								className={`text-charcoal-050 hover:text-white ${NAV_ROW_GRID_CLASS} p-md text-sm rounded ${FOCUS_VISIBLE_RING_OFFSET}`}
								to={routes.publicLogout.path}
							>
								<span className='text-center fa-solid fa-right-from-bracket'></span>
								<span>Logout</span>
							</Link>
						</div>
					</nav>
				</div>
				<div className='grow relative overflow-y-auto outline-none' data-testid='main-content'>
					<SmallScreenNotice />
					<Routes>
						<Route path={routes.publicProfiles.path} element={routes.publicProfiles.component()} />
						<Route path={routes.publicProfile.path} element={routes.publicProfile.component()} />
						<Route path={routes.publicReport.path} element={routes.publicReport.component()} />
						<Route path={routes.companySupport.path} element={<CompanySupport />} />
						<Route path={routes.companySettings.path} element={<PickerSettings />} />
						<Route path={routes.publicLogout.path} element={routes.publicLogout.component()} />
						<Route
							path='*'
							element={
								<SelectOrganisation
									fullName={userData.fullName}
									memberships={userData.memberships ?? []}
									showFictitiousOrganisations={userData.showFictitiousOrganisations}
								/>
							}
						/>
					</Routes>
					<Notifications />
				</div>
			</div>
		</div>
	)
}

export default OrganisationPickerLayout
